import { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Modal } from 'react-bootstrap-v5'
import * as Yup from 'yup'

import { OtpIcon } from '../../../svg/OtpIcon'
import { useFormik } from 'formik'
import { useMutation } from '@apollo/client'
import mutations from '../../../setup/graphql/mutations'
import Alert from '../../../_metronic/partials/alert'
import { validateUEN } from '../../../_metronic/helpers/validation'
import PhoneInput from 'react-phone-number-input'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { parsePhoneNumberWithError } from 'libphonenumber-js/max'

interface Props {
  show: boolean
  handleClose: any
  // goToStep:any;
}

const CompanyVerification: FC<Props> = ({
  show,
  handleClose,
}: // goToStep
any) => {
  const loanApplication = useSelector((state: any) => state.loan.loanApplication)

  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [alertType, setAlertType] = useState<any>('success')
  const [alertMsg, setAlertMsg] = useState<string>('')
  const [sendMailForKyb] = useMutation(mutations.SEND_MAIL_FOR_KYB)

  const history = useHistory()
  const formik = useFormik({
    initialValues: {
      businessOwnerName: loanApplication?.applicantName || '',
      businessOwnerEmail: loanApplication?.businessOwnerEmail || '',
      businessOwnerMobile: loanApplication?.mobileNumber || '',
      businessOwnerUEN: loanApplication?.companyUEN || '',
    },
    validationSchema: Yup.object().shape({
      businessOwnerName: Yup.string()
        .required('Business Owner Name is required')
        .matches(
          /^[A-Za-z\s.']+$/,
          'Please enter a valid Business Owner Name using letters, spaces, periods, or apostrophes, and include at least 2 letters.'
        )
        .test(
          'at-least-two-letters',
          'Business Owner Name cannot contain only special characters. Please enter a valid business owner name.',
          (value: any) => {
            if (!value) return false
            const letters = value.match(/[A-Za-z]/g)
            return (letters?.length || 0) >= 2
          }
        ),
      businessOwnerEmail: Yup.string()
        .email('Invalid Business Owner Email')
        .required('Business Owner Email is required'),
      businessOwnerMobile: Yup.string()
        .required('Business Owner Mobile Number is required')
        .test('mobileValidation', 'Please enter valid Singapore number', (value: any) => {
          if (!value) return false
          // Use libphonenumber-js to validate phone number format
          const number = parsePhoneNumberWithError(value, 'SG')
          // Use libphonenumber-js to validate phone number format
          return isValidPhoneNumber(value, 'SG') && number.isValid() && number.country === 'SG'
        }),
      businessOwnerUEN: Yup.string()
        .required('Company UEN is required')
        .test('unique', 'Invalid UEN. Kindly recheck the entered UEN', (value: any) =>
          validateUEN(value)
        ),
    }),
    onSubmit: (values) => {
      const { businessOwnerEmail, businessOwnerMobile, businessOwnerUEN, businessOwnerName } =
        values
      sendMailForKyb({
        variables: {
          data: JSON.stringify({
            agentCode: loanApplication?.agentCode,
            id: loanApplication.id,
            to: businessOwnerEmail,
            businessEmailAddress: businessOwnerEmail,
            mobileNumber: businessOwnerMobile,
            businessOwnerName: businessOwnerName,
            companyUEN: businessOwnerUEN,
          }),
        },
      })
        .then((response) => {
          setShowAlert(true)
          setAlertMsg(`Email is sent. Please inform ${businessOwnerName} to check the email`)
          setAlertType('success')
          setTimeout(() => {
            handleClose()
            history.push(`/agent-application/view/dashboard`)
          }, 2000)
        })
        .catch((error) => {
          console.error('Error sending email:', error)
        })
    },
  })

  return (
    <>
      <Modal
        className='bg-transparent'
        id='kt_mega_menu_modal'
        backdrop='static'
        keyboard={false}
        aria-hidden='true'
        role='dialog'
        dialogClassName='modal-dialog-centered mw-600px h-auto'
        tabIndex='-1'
        contentClassName='shadow-none'
        show={show}
        centered // Bootstrap's built-in class for centering modals
      >
        <div className='modal-content w-100 px-8'>
          <div className='d-flex flex-column align-items-center justify-content-center pt-6'>
            <OtpIcon className='mb-4' />
            <h2 className='section-heading m-0 mt-4 mb-2'>Business Details</h2>
            <p className='fs-4 mb-6 text-center' style={{ color: '#595959' }}>
              Send mail to business owner to verify their business details
            </p>
          </div>

          <div className='d-block mx-5 my-5'>
            <div className='d-flex align-items-center min-h-145px'>
              <div className='fv-row fv-plugins-icon-container w-100'>
                <div className='row loan-application'>
                  <div className='col-lg-6 col-md-6 col-sm-12 mb-4'>
                    <label className='w-100'>
                      <span className='input-field-label required mb-2 d-block'>
                        Business Owner Name
                      </span>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid'
                        placeholder='Enter Business Owner Name'
                        name='businessOwnerName'
                        value={formik.values.businessOwnerName}
                        onChange={formik.handleChange}
                      />
                      {formik.touched.businessOwnerName && formik.errors.businessOwnerName && (
                        <div className='text-danger mt-2'>{formik.errors.businessOwnerName}</div>
                      )}
                    </label>
                  </div>
                  <div className='col-lg-6 col-md-6 col-sm-12 mb-4'>
                    <label className='w-100'>
                      <span className='input-field-label required mb-2 d-block'>
                        Business Owner Email
                      </span>
                      <input
                        type='email'
                        className='form-control form-control-lg form-control-solid'
                        placeholder='Enter Business Owner Email'
                        name='businessOwnerEmail'
                        value={formik.values.businessOwnerEmail}
                        onChange={formik.handleChange}
                      />
                      {formik.touched.businessOwnerEmail && formik.errors.businessOwnerEmail && (
                        <div className='text-danger mt-2'>{formik.errors.businessOwnerEmail}</div>
                      )}
                    </label>
                  </div>

                  <div className='col-lg-6 col-md-6 col-sm-12 mb-4'>
                    <label htmlFor='' className='input-field-label required'>
                      Business Mobile Number
                    </label>
                    <PhoneInput
                      id='mobileNumber'
                      international
                      defaultCountry='SG'
                      value={formik.values.businessOwnerMobile}
                      onChange={(value) => formik.setFieldValue('businessOwnerMobile', value)}
                      placeholder='Enter phone number'
                      className='int-phone-input'
                    />
                    {(formik.touched.businessOwnerMobile ||
                      formik.values.businessOwnerMobile === undefined) &&
                      formik.errors.businessOwnerMobile && (
                        <div className='text-danger mt-2'>{formik.errors.businessOwnerMobile}</div>
                      )}
                  </div>
                  <div className='col-lg-6 col-md-6 col-sm-12 mb-4'>
                    <label className='w-100'>
                      <span className='input-field-label required mb-2 d-block'>Company UEN</span>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid'
                        placeholder='Enter Company UEN'
                        name='businessOwnerUEN'
                        value={formik.values.businessOwnerUEN}
                        onChange={formik.handleChange}
                      />
                      {formik.touched.businessOwnerUEN && formik.errors.businessOwnerUEN && (
                        <div className='text-danger mt-2'>{formik.errors.businessOwnerUEN}</div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='d-flex flex-column gap-5 flex-center mx-5 my-10'>
            <button
              className='button button-primary  w-100'
              id='button-primary'
              type='submit'
              onClick={() => formik.handleSubmit()}
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? 'Sending...' : 'Send Email'}
            </button>
            <div className='line divider d-flex justify-content-center text-muted'>
              <span className='zIndex-1'>OR</span>
            </div>
            <button className='button button-outline-back w-100' onClick={() => handleClose()}>
              Back
            </button>
          </div>
        </div>
      </Modal>
      {showAlert && (
        <Alert
          position='top-center'
          variant={alertType}
          showAlert={showAlert}
          setShowAlert={setShowAlert}
        >
          {alertMsg}
        </Alert>
      )}
    </>
  )
}

export default CompanyVerification
